import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  query, where, orderBy, onSnapshot, serverTimestamp, addDoc, limit,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import { orgCollection, orgDoc } from "./orgPaths";

// ===== ORGANIZATION / TEAM =====

/**
 * Get all team members in the current user's organization
 */
export async function getTeamMembers(organizationId) {
  try {
    const q = query(
      collection(db, "users"),
      where("organization", "==", organizationId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (e) {
    console.error("Error fetching team:", e);
    return [];
  }
}

/**
 * Subscribe to real-time team member updates
 */
export function onTeamMembers(organizationId, callback) {
  const q = query(
    collection(db, "users"),
    where("organization", "==", organizationId)
  );
  return onSnapshot(q, (snapshot) => {
    const members = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(members);
  }, (error) => {
    console.error("Team listener error:", error);
    callback([]);
  });
}

/**
 * Update a user's role (supervisor action)
 */
export async function updateUserRole(userId, newRole) {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, { role: newRole, updatedAt: serverTimestamp() });
    return { success: true };
  } catch (e) {
    console.error("Error updating role:", e);
    return { success: false, error: e.message };
  }
}

/**
 * Update a user's status (active, deactivated)
 */
export async function updateUserStatus(userId, status) {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, { status, updatedAt: serverTimestamp() });
    return { success: true };
  } catch (e) {
    console.error("Error updating status:", e);
    return { success: false, error: e.message };
  }
}

/**
 * Get organization info
 */
export async function getOrganization(orgId) {
  try {
    const orgRef = doc(db, "organizations", orgId);
    const snap = await getDoc(orgRef);
    if (snap.exists()) return { id: snap.id, ...snap.data() };
    return null;
  } catch (e) {
    console.error("Error fetching org:", e);
    return null;
  }
}

/**
 * Create or update organization
 */
export async function saveOrganization(orgData) {
  try {
    const orgId = orgData.id || orgData.name.toLowerCase().replace(/\s+/g, "-");
    const orgRef = doc(db, "organizations", orgId);
    await setDoc(orgRef, {
      ...orgData,
      id: orgId,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    return { success: true, id: orgId };
  } catch (e) {
    console.error("Error saving org:", e);
    return { success: false, error: e.message };
  }
}


// ===== SHARED NOTES (Firestore) =====

/**
 * Save a note to the shared Firestore collection
 * Called when an agent submits a note — makes it visible to supervisors
 */
export async function saveSharedNote(noteData) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");

    const noteRef = await addDoc(orgCollection("notes"), {
      ...noteData,
      agentId: user.uid,
      agentName: user.displayName || user.email,
      agentEmail: user.email,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      supervisorReviewed: false,
      supervisorSignedBy: null,
      supervisorSignedAt: null,
      safetyReviewed: false,
    });
    return { success: true, id: noteRef.id };
  } catch (e) {
    console.error("Error saving shared note:", e);
    return { success: false, error: e.message };
  }
}

/**
 * Subscribe to all shared notes for an organization
 */
export function onSharedNotes(organizationId, callback) {
  const q = query(
    orgCollection("notes", organizationId),
    orderBy("createdAt", "desc"),
    limit(100)
  );
  return onSnapshot(q, (snapshot) => {
    const notes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(notes);
  }, (error) => {
    console.error("Shared notes listener error:", error);
    callback([]);
  });
}

/**
 * Supervisor signs/approves a note — adds to message thread
 */
export async function signNote(noteId, supervisorName) {
  try {
    const noteRef = orgDoc("notes", noteId);
    const noteSnap = await getDoc(noteRef);
    const existing = noteSnap.exists() ? noteSnap.data() : {};
    const thread = existing.messageThread || [];

    thread.push({
      id: `msg-${Date.now()}`,
      from: supervisorName,
      role: "supervisor",
      type: "signed",
      text: "Note co-signed and approved.",
      timestamp: new Date().toISOString(),
    });

    await updateDoc(noteRef, {
      supervisorReviewed: true,
      supervisorSignedBy: supervisorName,
      supervisorSignedAt: serverTimestamp(),
      status: "signed",
      messageThread: thread,
    });
    return { success: true };
  } catch (e) {
    console.error("Error signing note:", e);
    return { success: false, error: e.message };
  }
}

/**
 * Request revision on a note — adds to message thread
 */
export async function requestRevision(noteId, reason, supervisorName) {
  try {
    const noteRef = orgDoc("notes", noteId);
    const noteSnap = await getDoc(noteRef);
    const existing = noteSnap.exists() ? noteSnap.data() : {};
    const thread = existing.messageThread || [];

    thread.push({
      id: `msg-${Date.now()}`,
      from: supervisorName,
      role: "supervisor",
      type: "revision-request",
      text: reason,
      timestamp: new Date().toISOString(),
    });

    await updateDoc(noteRef, {
      status: "revision-requested",
      revisionReason: reason,
      revisionRequestedBy: supervisorName,
      revisionRequestedAt: serverTimestamp(),
      messageThread: thread,
    });
    return { success: true };
  } catch (e) {
    console.error("Error requesting revision:", e);
    return { success: false, error: e.message };
  }
}

/**
 * Add a message to a note's thread (supervisor or agent)
 */
export async function addNoteMessage(noteId, text, senderName, senderRole) {
  try {
    const noteRef = orgDoc("notes", noteId);
    const noteSnap = await getDoc(noteRef);
    const existing = noteSnap.exists() ? noteSnap.data() : {};
    const thread = existing.messageThread || [];

    thread.push({
      id: `msg-${Date.now()}`,
      from: senderName,
      role: senderRole,
      type: "message",
      text,
      timestamp: new Date().toISOString(),
    });

    await updateDoc(noteRef, { messageThread: thread });
    return { success: true };
  } catch (e) {
    console.error("Error adding note message:", e);
    return { success: false, error: e.message };
  }
}

/**
 * Agent resubmits a revised note
 */
export async function resubmitNote(noteId, updatedNoteData, agentName) {
  try {
    const noteRef = orgDoc("notes", noteId);
    const noteSnap = await getDoc(noteRef);
    const existing = noteSnap.exists() ? noteSnap.data() : {};
    const thread = existing.messageThread || [];

    thread.push({
      id: `msg-${Date.now()}`,
      from: agentName,
      role: "agent",
      type: "resubmission",
      text: "Note revised and resubmitted.",
      timestamp: new Date().toISOString(),
    });

    await updateDoc(noteRef, {
      status: "submitted",
      noteData: updatedNoteData,
      messageThread: thread,
      resubmittedAt: serverTimestamp(),
      resubmittedBy: agentName,
      revisionReason: null,
    });
    return { success: true };
  } catch (e) {
    console.error("Error resubmitting note:", e);
    return { success: false, error: e.message };
  }
}

/**
 * Mark safety alert as reviewed
 */
export async function reviewSafetyAlert(noteId, supervisorName) {
  try {
    const noteRef = orgDoc("notes", noteId);
    await updateDoc(noteRef, {
      safetyReviewed: true,
      safetyReviewedBy: supervisorName,
      safetyReviewedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (e) {
    console.error("Error reviewing safety alert:", e);
    return { success: false, error: e.message };
  }
}


// ===== SAFETY ALERTS =====

/**
 * Subscribe to safety alerts for an organization
 */
export function onSafetyAlerts(organizationId, callback) {
  const q = query(
    orgCollection("notes", organizationId),
    where("hasPriorityFlags", "==", true),
    orderBy("createdAt", "desc"),
    limit(50)
  );
  return onSnapshot(q, (snapshot) => {
    const alerts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(alerts);
  }, (error) => {
    console.error("Safety alerts listener error:", error);
    callback([]);
  });
}


// ===== AGENT STATUS =====

/**
 * Update agent's live status (in-session, available, documenting, traveling)
 */
export async function updateAgentStatus(status, currentIndividual = null) {
  try {
    const user = auth.currentUser;
    if (!user) return;

    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, {
      liveStatus: status,
      currentIndividual: currentIndividual,
      lastStatusUpdate: serverTimestamp(),
    });
  } catch (e) {
    console.error("Error updating agent status:", e);
  }
}